import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LOGO } from "@/assets";
import { api, formatError } from "@/lib/api";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { openFeedback } from "@/components/FeedbackModal";

const HERO = process.env.REACT_APP_BETA_HERO_URL || "https://images.unsplash.com/photo-1482649671545-bc53dcf1ad7c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
// Same stack being wired on /join; renders in Black Ops One until Routhem @font-face lands.
const ROUTHEM = { fontFamily: "'Routhem', 'Black Ops One', 'Barlow Condensed', sans-serif" };

// Redacted "black bar" — real copy lives on the paragraph's aria-label for a11y.
function Redact({ w }) {
  return <span aria-hidden="true" className="inline-block align-middle bg-black mx-1 ring-1 ring-inset ring-[#849064]/30" style={{ width: w, height: "0.95em" }} />;
}

export default function BetaSplash({ onUnlock, full = false }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // When the beta is full we lead with the waitlist, but keep the invite-code
  // path available (per-invite codes bypass the global seat cap).
  const [showCode, setShowCode] = useState(!full);
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setError("Enter your beta access code."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/beta/verify", { code });
      // No persistence: unlocking only affects the current page-load session.
      // The gate reappears on the next fresh visit by design.
      onUnlock();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Invalid access code.");
    } finally {
      setBusy(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid email address."); return; }
    setWlBusy(true);
    setError("");
    try {
      await api.post("/beta/waitlist", { email });
      setJoined(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setWlBusy(false);
    }
  };

  const inputCls = "w-full bg-black/50 border border-[#849064]/30 focus:border-[#849064] outline-none px-4 py-3.5 font-mono tracking-[0.2em] uppercase text-[#e6e6e6] transition-colors placeholder:text-[#849064]/40";
  const btnCls = "w-full inline-flex items-center justify-center gap-2 bg-[#6f7a56] hover:bg-[#849064] disabled:opacity-50 text-[#e6e6e6] font-mono font-bold tracking-[0.25em] uppercase text-sm py-3.5 transition-colors";

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden px-4 py-8" style={{ background: "#0a0b09" }}>
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/80" />
      {/* vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 42%, transparent 0%, rgba(10,11,9,0.55) 55%, #0a0b09 100%)" }} />
      {/* scanlines */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "repeating-linear-gradient(to bottom, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 1px, transparent 1px, transparent 3px)", opacity: 0.4 }} />
      {/* film grain */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", opacity: 0.06, mixBlendMode: "overlay" }} />

      <div className="relative z-10 w-full max-w-2xl animate-fade-up">
        <div className="border border-[#849064]/25 bg-black/55 backdrop-blur-[2px]" data-testid="dossier-frame">
          {/* document header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-2.5 border-b border-[#849064]/25 font-mono text-[9px] sm:text-[10px] tracking-[0.2em] text-[#849064]">
            <span>FILE: OS-0001-BETA</span>
            <span>ACCESS LEVEL: RESTRICTED</span>
          </div>

          {/* content */}
          <div className="px-5 sm:px-12 py-9 sm:py-11 text-center">
            <Link to="/" aria-label="OpticSwap home" className="inline-block">
              <img src={LOGO} alt="OpticSwap" className="w-24 sm:w-28 h-auto mx-auto" data-testid="beta-logo" />
            </Link>

            <div
              className="inline-block border-2 border-[#b0443c] text-[#b0443c] font-mono font-bold text-sm tracking-[0.35em] px-4 py-1 mt-6"
              style={{ transform: "rotate(-4deg)" }}
            >
              CLASSIFIED
            </div>

            <h1 style={ROUTHEM} className="font-heading text-4xl sm:text-5xl font-black tracking-tight mt-6 leading-[0.95]">
              {full ? (
                <><span className="text-[#e6e6e6]">The current beta</span><br /><span className="text-[#849064]">is full.</span></>
              ) : (
                <><span className="text-[#e6e6e6]">OpticSwap is in</span><br /><span className="text-[#849064]">private beta.</span></>
              )}
            </h1>

            {full ? (
              <p className="mt-5 font-mono text-[11px] sm:text-xs tracking-[0.1em] uppercase leading-relaxed text-[#849064]/90 max-w-md mx-auto">
                We've hit our tester cap for now. Submit your email to the waitlist and we'll signal you the moment a slot declassifies.
              </p>
            ) : (
              <p
                aria-label="the [REDACTED] [REDACTED] [REDACTED] thermal, night vision, day optics, and [REDACTED]. [REDACTED] is denied for now."
                className="mt-5 font-mono text-[11px] sm:text-xs tracking-[0.1em] uppercase leading-loose text-[#849064]/90 max-w-md mx-auto"
              >
                <span aria-hidden="true">
                  the <Redact w="66px" /> <Redact w="104px" /> <Redact w="52px" /> thermal, night vision, day optics, and <Redact w="120px" />. <Redact w="84px" /> is denied for now.
                </span>
              </p>
            )}

            {/* clearance divider */}
            <div className="flex items-center gap-3 my-7">
              <div className="flex-1 h-px bg-[#849064]/25" />
              <span className="font-mono text-[10px] tracking-[0.35em] text-[#6f7a56] whitespace-nowrap">CLEARANCE REQUIRED</span>
              <div className="flex-1 h-px bg-[#849064]/25" />
            </div>

            {error && (
              <div data-testid="beta-error" className="border border-[#b0443c]/60 bg-[#b0443c]/10 text-[#b0443c] text-xs px-3 py-2 font-mono tracking-wide mb-4 text-left">
                {error}
              </div>
            )}

            {/* waitlist (beta full) */}
            {full && (
              joined ? (
                <div data-testid="waitlist-joined" className="border border-[#6f7a56]/50 bg-[#6f7a56]/10 text-[#849064] text-xs px-3 py-4 font-mono tracking-wide flex flex-col items-center gap-2">
                  <CheckCircle2 className="w-6 h-6" /> LOGGED. STANDBY FOR SIGNAL.
                </div>
              ) : (
                <form onSubmit={joinWaitlist} className="space-y-3">
                  <label className="block text-left font-mono text-[11px] tracking-[0.15em] text-[#849064]">&gt; SUBMIT EMAIL FOR WAITLIST_</label>
                  <input
                    data-testid="waitlist-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="operative@email.com"
                    className={inputCls + " tracking-[0.1em] lowercase"}
                  />
                  <button data-testid="waitlist-submit" disabled={wlBusy} className={btnCls}>
                    {wlBusy ? "TRANSMITTING..." : <>JOIN WAITLIST <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )
            )}

            {/* access code */}
            {showCode && (
              <form onSubmit={submit} className={`space-y-3 ${full ? "mt-5" : ""}`}>
                <label htmlFor="beta-code" className="block text-left font-mono text-[11px] tracking-[0.15em] text-[#849064]">
                  &gt; ENTER ACCESS CODE TO PROCEED_
                </label>
                <input
                  id="beta-code"
                  data-testid="beta-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="••••••••••"
                  autoFocus={!full}
                  className={inputCls + " tracking-[0.35em]"}
                />
                <div data-testid="beta-scarcity" className="flex items-center gap-2 text-left">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#b0443c] animate-pulse shrink-0" aria-hidden="true" />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#b0443c]">Limited clearance slots remaining</span>
                </div>
                <button data-testid="beta-submit" disabled={busy} className={btnCls}>
                  {busy ? "DECRYPTING..." : "Login"}
                </button>
                {!full && (
                  <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#849064]/60 pt-1">
                    Next: create your account or log in
                  </p>
                )}
              </form>
            )}

            {full && !showCode && (
              <button
                data-testid="beta-have-code"
                onClick={() => { setShowCode(true); setError(""); }}
                className="mt-6 font-mono text-[11px] tracking-[0.15em] uppercase text-[#849064] hover:text-[#e6e6e6] transition-colors"
              >
                &gt; HAVE AN INVITE CODE?
              </button>
            )}

            <div className="mt-7">
              <button
                onClick={openFeedback}
                data-testid="beta-feedback-link"
                className="font-mono text-[11px] tracking-[0.1em] underline underline-offset-4 text-[#849064] hover:text-[#e6e6e6] transition-colors"
              >
                // SUBMIT FIELD REPORT (FEEDBACK)
              </button>
            </div>
          </div>

          {/* document footer */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-2.5 border-t border-[#849064]/25 font-mono text-[9px] sm:text-[10px] tracking-[0.2em] text-[#849064]">
            <span>OPTICSWAP INTEL DIV.</span>
            <span>DECLASSIFY ON: 09.04.2026</span>
          </div>
        </div>
      </div>
    </div>
  );
}
